import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X } from "lucide-react";

/**
 * Lightweight install prompt for installable PWAs.
 * Shows a small bottom-right toast-like card when the browser fires
 * `beforeinstallprompt`. iOS users see a contextual hint instead.
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem("pwa-install-dismissed");
    const installed =
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches;
    if (installed) return;

    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferred(e);
      if (!dismissed) setVisible(true);
    }
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS standalone detection (no beforeinstallprompt support on iOS Safari)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    if (isIOS && !dismissed && !window.navigator.standalone) {
      setTimeout(() => setVisible(true), 4000);
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  async function install() {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    if (choice.outcome === "accepted") {
      localStorage.setItem("pwa-install-dismissed", "installed");
    }
    setDeferred(null);
    setVisible(false);
  }

  function dismiss() {
    localStorage.setItem("pwa-install-dismissed", "1");
    setVisible(false);
  }

  if (!visible) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-xs bg-white border border-slate-200 shadow-xl rounded-lg p-3 print:hidden"
      data-testid="pwa-install-prompt"
    >
      <button
        className="absolute top-1.5 right-1.5 text-slate-400 hover:text-slate-600"
        onClick={dismiss}
        aria-label="Fechar"
        data-testid="pwa-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-2.5">
        <div className="bg-blue-100 rounded-full p-2">
          <Download className="h-4 w-4 text-blue-700" />
        </div>
        <div className="pr-4">
          <div className="font-medium text-sm text-slate-900">
            Instalar BV Médicos
          </div>
          {isIOS ? (
            <p className="text-xs text-slate-600 mt-1 leading-snug">
              No Safari, toque em <span className="font-medium">Compartilhar</span> →{" "}
              <span className="font-medium">Adicionar à Tela de Início</span>.
            </p>
          ) : (
            <p className="text-xs text-slate-600 mt-1 leading-snug">
              Acesse mais rápido. Funciona como um app nativo no seu celular.
            </p>
          )}
          {!isIOS && deferred && (
            <Button
              size="sm"
              className="bg-blue-700 hover:bg-blue-800 mt-2 h-7 text-xs"
              onClick={install}
              data-testid="pwa-install-btn"
            >
              <Download className="h-3 w-3 mr-1" /> Instalar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
